import { useRef, useCallback } from 'react';
import { getFirestore, doc, setDoc, Timestamp } from 'firebase/firestore';
import logger from '../utils/logger';

// Global instance invocation counter (shared across all hook instances in this browser session)
let GLOBAL_INSTANCE_INVOCATION_COUNT = 0;
let INSTANCE_RESET_TIME = Date.now();

const RATE_LIMITS = {
  ARCHIVE: {
    maxPerWindow: 5,
    windowMs: 5000,
    cooldownAfterTrip: 30000
  },
  RESTORE: {
    maxPerWindow: 5,
    windowMs: 5000,
    cooldownAfterTrip: 30000
  },
  DELETE: {
    maxPerWindow: 3,
    windowMs: 5000,
    cooldownAfterTrip: 45000
  },
  DUPLICATE_AUDIT: {
    maxPerWindow: 1,
    windowMs: 30000
  }
};

const CIRCUIT_BREAKER_CONFIG = {
  triggerThreshold: 5,
  timeWindow: 10000,
  tripDuration: 30000
};

const OPERATION_GUARDS = {
  ARCHIVE: {
    recentArchiveCooldown: 60000
  },
  RESTORE: {
    recentRestoreCooldown: 60000
  }
};

export const useOperationRateLimiter = () => {
  const db = getFirestore();
  
  // Local refs for this hook instance
  const operationLog = useRef(new Map()); // billId -> [{ timestamp, type, status }, ...]
  const circuitBreaker = useRef({
    isTripped: false,
    tripTime: null,
    violatingBillId: null,
    reason: null
  });
  
  // In-memory user session tracking (avoids Firestore reads)
  const userSessionOps = useRef([]);  // Array of { timestamp, type, billId }

  // Update instance invocation counter
  const updateInstanceCounter = useCallback(() => {
    const now = Date.now();
    // Reset counter if more than 60 seconds have passed
    if (now - INSTANCE_RESET_TIME > 60000) {
      GLOBAL_INSTANCE_INVOCATION_COUNT = 0;
      INSTANCE_RESET_TIME = now;
    }
    GLOBAL_INSTANCE_INVOCATION_COUNT++;
    return GLOBAL_INSTANCE_INVOCATION_COUNT;
  }, []);

  // Layer 2: Event Age Breaker
  const checkEventAge = useCallback((operationTimestamp) => {
    const now = Date.now();
    const age = now - operationTimestamp;
    const maxAge = 10000; // 10 seconds

    if (age > maxAge) {
      const error = `[CRITICAL] Stale event detected: Operation is ${Math.round(age / 1000)}s old (max: 10s)`;
      logger.error(error);
      return {
        blocked: true,
        reason: 'Event age exceeded 10 seconds',
        errorDescription: error
      };
    }
    return { blocked: false };
  }, []);

  // Layer 3: Emergency Instance Breaker
  const checkInstanceBreaker = useCallback(() => {
    const instanceCount = updateInstanceCounter();
    const maxPerMinute = 50;

    if (instanceCount > maxPerMinute) {
      const error = `[CRITICAL] Instance breaker triggered: ${instanceCount} operations in this session (max: ${maxPerMinute}/min)`;
      logger.error(error);
      return {
        blocked: true,
        reason: 'Instance operation limit exceeded',
        errorDescription: error
      };
    }
    return { blocked: false };
  }, [updateInstanceCounter]);

  // Layer 4: User Session Governor (IN-MEMORY - no Firestore reads!)
  const checkUserSessionGovernor = useCallback((userEmail) => {
    if (!userEmail) return { blocked: false };

    const now = Date.now();
    const maxOpsPerWindow = 20;
    const windowMs = 60000; // 60 seconds

    // Clean old operations from memory
    userSessionOps.current = userSessionOps.current.filter(op => now - op.timestamp < windowMs);

    if (userSessionOps.current.length >= maxOpsPerWindow) {
      const error = `[CRITICAL] User session governor triggered: ${userSessionOps.current.length} operations in 60 seconds (max: ${maxOpsPerWindow})`;
      logger.error(error);
      return {
        blocked: true,
        reason: 'User operation limit exceeded (20 ops per 60s)',
        errorDescription: error
      };
    }

    return { blocked: false };
  }, []);

  // Layer 1: Idempotency Check
  const checkIdempotency = useCallback((billBefore, billAfter, operationType) => {
    if (!billBefore || !billAfter) return { isDuplicate: false };

    const beforeArchived = billBefore.archived || false;
    const afterArchived = billAfter.archived || false;
    const beforeArchivedAt = billBefore.archivedAt;
    const afterArchivedAt = billAfter.archivedAt;

    // For restore/archive, check if archived status actually changed
    if (operationType === 'RESTORE' || operationType === 'ARCHIVE') {
      if (beforeArchived === afterArchived && beforeArchivedAt === afterArchivedAt) {
        logger.warn('[IDEMPOTENCY] Skipping no-op operation:', operationType, billBefore.id);
        return {
          isDuplicate: true,
          reason: 'No state change detected'
        };
      }
    }

    // For delete, verify bill actually exists before operation
    if (operationType === 'DELETE' && !billBefore) {
      return {
        isDuplicate: true,
        reason: 'Bill does not exist'
      };
    }

    return { isDuplicate: false };
  }, []);

  // Check if operation can proceed through all 4 layers
  const canPerformOperation = useCallback(async (operationType, billId, userEmail, operationTimestamp = Date.now(), billBefore = null, billAfter = null) => {
    // Check circuit breaker first
    if (circuitBreaker.current.isTripped) {
      const now = Date.now();
      const timeSinceTrigger = now - circuitBreaker.current.tripTime;
      const cooldown = CIRCUIT_BREAKER_CONFIG.tripDuration;

      if (timeSinceTrigger < cooldown) {
        const waitSeconds = Math.ceil((cooldown - timeSinceTrigger) / 1000);
        const error = `[CRITICAL] Circuit breaker is tripped: Too many ${operationType} operations on bill-${billId}. Wait ${waitSeconds}s.`;
        logger.error(error);
        return {
          allowed: false,
          reason: 'Circuit breaker tripped',
          errorDescription: error,
          waitTime: waitSeconds,
          showAdminModal: true
        };
      } else {
        // Auto-reset after cooldown
        circuitBreaker.current.isTripped = false;
        circuitBreaker.current.tripTime = null;
      }
    }

    // Layer 1: Idempotency Check
    if (billBefore && billAfter) {
      const idempotencyCheck = checkIdempotency(billBefore, billAfter, operationType);
      if (idempotencyCheck.isDuplicate) {
        logger.warn('[IDEMPOTENCY] Skipping duplicate operation');
        return {
          allowed: false,
          reason: 'Idempotency check: operation would have no effect',
          skip: true // Skip silently without showing error
        };
      }
    }

    // Layer 2: Event Age Breaker
    const ageCheck = checkEventAge(operationTimestamp);
    if (ageCheck.blocked) {
      return {
        allowed: false,
        reason: ageCheck.reason,
        errorDescription: ageCheck.errorDescription,
        showAdminModal: true
      };
    }

    // Layer 3: Emergency Instance Breaker
    const instanceCheck = checkInstanceBreaker();
    if (instanceCheck.blocked) {
      return {
        allowed: false,
        reason: instanceCheck.reason,
        errorDescription: instanceCheck.errorDescription,
        showAdminModal: true
      };
    }

    // Layer 4: User Session Governor (now synchronous - no Firestore read)
    const userCheck = checkUserSessionGovernor(userEmail);
    if (userCheck.blocked) {
      return {
        allowed: false,
        reason: userCheck.reason,
        errorDescription: userCheck.errorDescription,
        showAdminModal: true
      };
    }

    return { allowed: true };
  }, [checkEventAge, checkInstanceBreaker, checkUserSessionGovernor, checkIdempotency]);

  // Log system error to Firestore (define before any callback deps reference it)
  const logSystemError = useCallback(async (errorConfig) => {
    try {
      const errorRef = doc(db, 'system_errors', `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
      const now = Date.now();
      const expiresAtTimestamp = new Date(now + 30 * 24 * 60 * 60 * 1000); // 30 days
      
      await setDoc(errorRef, {
        timestamp: now,
        iso8601: new Date(now).toISOString(),
        errorType: errorConfig.errorType,
        description: errorConfig.description,
        billId: errorConfig.billId,
        operationType: errorConfig.operationType,
        userEmail: errorConfig.userEmail,
        operationCount: errorConfig.operationCount || 0,
        cascadeDepth: errorConfig.cascadeDepth || 0,
        timeWindowMs: errorConfig.timeWindowMs || 0,
        status: 'active',
        expiresAt: Timestamp.fromDate(expiresAtTimestamp)
      });
    } catch (error) {
      logger.error('[ERROR] Failed to log system error to Firestore:', error);
    }
  }, [db]);

  // Record operation and check for circuit breaker trigger
  const recordOperation = useCallback(async (operationType, billId, resultStatus, userEmail) => {
    const timestamp = Date.now();

    // Initialize log for this bill if not exists
    if (!operationLog.current.has(billId)) {
      operationLog.current.set(billId, []);
    }

    const log = operationLog.current.get(billId);
    log.push({ timestamp, operationType, resultStatus });

    // Keep only last 20 operations per bill
    if (log.length > 20) {
      log.shift();
    }

    // Check for circuit breaker trigger: 5+ identical ops on same bill in 10s
    const recentOps = log.filter(op => 
      op.operationType === operationType && 
      (timestamp - op.timestamp) < CIRCUIT_BREAKER_CONFIG.timeWindow &&
      op.resultStatus === 'success'
    );

    if (recentOps.length >= CIRCUIT_BREAKER_CONFIG.triggerThreshold) {
      const error = `[CRITICAL] Circuit breaker triggered: ${recentOps.length} ${operationType} operations on bill-${billId} within 10 seconds`;
      logger.error(error);
      
      circuitBreaker.current.isTripped = true;
      circuitBreaker.current.tripTime = timestamp;
      circuitBreaker.current.violatingBillId = billId;
      circuitBreaker.current.reason = error;

      // Log to system_errors
      await logSystemError({
        errorType: 'CIRCUIT_BREAKER',
        description: error,
        billId,
        operationType,
        userEmail,
        operationCount: recentOps.length,
        cascadeDepth: 0,
        timeWindowMs: CIRCUIT_BREAKER_CONFIG.timeWindow
      });

      return { triggered: true, errorDescription: error };
    }

    // Track in memory for session governor (no Firestore read!)
    if (userEmail && resultStatus === 'success') {
      userSessionOps.current.push({ timestamp, type: operationType, billId });
      
      // Optional: Write to Firestore for analytics (write-only, no read)
      // This is async fire-and-forget - doesn't block the operation
      try {
        const rateLimitRef = doc(db, 'rate_limits', userEmail);
        // Use setDoc with merge to avoid reading first
        setDoc(rateLimitRef, {
          userId: userEmail,
          lastReset: Timestamp.now(),
          lastOperation: { timestamp, type: operationType, billId }
        }, { merge: true }).catch(err => logger.warn('[WARN] Failed to log rate limit:', err));
      } catch (error) {
        // Silently fail - this is just for analytics
        logger.warn('[WARN] Rate limit write failed:', error);
      }
    }

    return { triggered: false };
  }, [db, logSystemError]);

  // Get circuit breaker status
  const getCircuitBreakerStatus = useCallback(() => {
    if (!circuitBreaker.current.isTripped) {
      return { isTripped: false };
    }

    const now = Date.now();
    const timeSinceTrigger = now - circuitBreaker.current.tripTime;
    const timeUntilReset = CIRCUIT_BREAKER_CONFIG.tripDuration - timeSinceTrigger;

    return {
      isTripped: true,
      reason: circuitBreaker.current.reason,
      violatingBillId: circuitBreaker.current.violatingBillId,
      tripTime: new Date(circuitBreaker.current.tripTime),
      timeUntilReset: Math.max(0, timeUntilReset)
    };
  }, []);

  // Reset circuit breaker (for testing/admin)
  const resetCircuitBreaker = useCallback(() => {
    circuitBreaker.current.isTripped = false;
    circuitBreaker.current.tripTime = null;
    circuitBreaker.current.violatingBillId = null;
    circuitBreaker.current.reason = null;
    logger.warn('[ADMIN] Circuit breaker manually reset');
  }, []);

  // Get operation log
  const getOperationLog = useCallback(() => {
    const allOps = [];
    operationLog.current.forEach((ops, billId) => {
      ops.forEach(op => {
        allOps.push({ billId, ...op });
      });
    });
    return allOps.sort((a, b) => b.timestamp - a.timestamp).slice(0, 50);
  }, []);

  // Check if operation is in progress
  const isOperationInProgress = useCallback((billId) => {
    const log = operationLog.current.get(billId);
    if (!log || log.length === 0) return false;
    
    const lastOp = log[log.length - 1];
    const now = Date.now();
    return lastOp.status === 'pending' && (now - lastOp.timestamp) < 30000; // 30s timeout
  }, []);

  return {
    canPerformOperation,
    recordOperation,
    getCircuitBreakerStatus,
    resetCircuitBreaker,
    getOperationLog,
    isOperationInProgress,
    logSystemError
  };
};
